import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Overlay } from "@/components/Overlay";
import { GameHeader, BetControls } from "@/components/games/GameShell";
import { api, errMsg, fmt } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const SUIT = { S: ["♠", "#E5E7EB"], H: ["♥", "#EF4444"], D: ["♦", "#EF4444"], C: ["♣", "#E5E7EB"] };

function Card({ c, hidden, i }) {
  if (hidden || c === "??") {
    return <div data-testid="bj-card-hidden" className="w-14 h-20 rounded-lg bg-gradient-to-br from-[#2A3BA0] to-[#141B4D] border border-white/20 shadow-lg" style={{ marginLeft: i ? -18 : 0 }} />;
  }
  const rank = c.slice(0, -1); const [sym, color] = SUIT[c.slice(-1)];
  return (
    <motion.div initial={{ rotateY: 90, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }} transition={{ duration: 0.25 }}
      data-testid={`bj-card-${c}`} className="w-14 h-20 rounded-lg bg-white shadow-lg flex flex-col items-center justify-center font-black" style={{ color, marginLeft: i ? -18 : 0 }}>
      <span className="text-lg leading-none">{rank}</span><span className="text-xl leading-none">{sym}</span>
    </motion.div>
  );
}

function Hand({ cards, hidden, label, value, testid }) {
  return (
    <div className="flex flex-col items-center gap-2" data-testid={testid}>
      <div className="flex items-center">{cards.map((c, i) => <Card key={c === "??" ? "hidden" : c} c={c} hidden={hidden && i === 1} i={i} />)}</div>
      <span className="rounded-full bg-black/40 px-3 py-0.5 text-sm font-bold" data-testid={`${testid}-value`}>{label}: {value}</span>
    </div>
  );
}

export function BlackjackModal({ game, onClose }) {
  const { currency, setUser } = useApp();
  const [bet, setBet] = useState(currency === "gc" ? 100 : 1);
  useEffect(() => { setBet(currency === "gc" ? 100 : 1); }, [currency, setBet]);
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const apply = (data) => { setState(data); if (data.user) setUser(data.user); };
  const call = async (path, body) => {
    setBusy(true);
    try { const { data } = await api.post(`/games/blackjack/${path}`, body); apply(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const start = () => call("start", { currency, bet: Number(bet), game_id: game.id });
  const done = state && state.status === "done";

  const RESULT = { win: ["You win!", "sc-text"], blackjack: ["Blackjack! 3:2", "gc-text"], push: ["Push — bet returned", "text-gray-300"], lose: ["Dealer wins", "text-[#EF4444]"] };

  return (
    <Overlay open={!!game} onClose={() => { setState(null); onClose(); }} testId="blackjack-modal" className="max-w-lg">
      {game && (
        <div>
          <GameHeader game={game} />
          <div className="p-5 bg-gradient-to-b from-[#0d3b2e] via-[#0a2b22] to-[#0B0E14]">
            <div className="rounded-2xl bg-[#0a2b22]/60 border border-[#10B981]/20 p-5 min-h-[260px] flex flex-col justify-between" style={{ backgroundImage: "radial-gradient(ellipse at top, rgba(16,185,129,0.12), transparent)" }}>
              {state ? (
                <>
                  <Hand cards={state.dealer} hidden={!done && state.status === "active"} label="Dealer" value={done ? state.dealer_value : state.dealer_value} testid="bj-dealer" />
                  <div className="h-8 flex items-center justify-center font-display text-xl font-black" data-testid="bj-result">
                    {done && <span className={RESULT[state.result]?.[1]}>{RESULT[state.result]?.[0]}{state.win > 0 ? ` +${fmt(state.win, state.currency)} ${state.currency.toUpperCase()}` : ""}</span>}
                  </div>
                  <Hand cards={state.player} label="You" value={state.player_value} testid="bj-player" />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Place your bet and deal to start</div>
              )}
            </div>

            {state && state.status === "active" ? (
              <div className="mt-4 grid grid-cols-3 gap-2">
                <button data-testid="bj-hit-btn" disabled={busy} onClick={() => call("hit", { game_id: state.game_id })} className="rounded-xl bg-[#00A3FF] py-3.5 font-display font-black disabled:opacity-50 hover:brightness-110 transition-[filter]">HIT</button>
                <button data-testid="bj-stand-btn" disabled={busy} onClick={() => call("stand", { game_id: state.game_id })} className="rounded-xl bg-[#FFB800] text-[#0B0E14] py-3.5 font-display font-black disabled:opacity-50 hover:brightness-110 transition-[filter]">STAND</button>
                <button data-testid="bj-double-btn" disabled={busy || !state.can_double} onClick={() => call("double", { game_id: state.game_id })} className="rounded-xl bg-[#10B981] text-[#0B0E14] py-3.5 font-display font-black disabled:opacity-40 hover:brightness-110 transition-[filter]">DOUBLE</button>
              </div>
            ) : (
              <>
                <div className="mt-4"><BetControls bet={bet} setBet={setBet} disabled={busy} /></div>
                <button data-testid="bj-deal-btn" disabled={busy} onClick={start} className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl bg-[#FFB800] text-[#0B0E14] py-4 font-display text-xl font-black hover:brightness-110 active:scale-[0.98] disabled:opacity-60 transition-[filter,transform,opacity]">
                  {busy ? <Loader2 className="animate-spin" /> : done ? "DEAL AGAIN" : "DEAL"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </Overlay>
  );
}
